// deleted_capture_items — forensic archive for admin hard-delete (AE3).
//
// ADR-017 (docs/adr/017-admin-hard-delete-capture-items.md, Accepted)
// reverses the "never physically drop a captured count" rule for the
// admin hard-delete path, but only as ARCHIVE-then-delete: the row is
// copied here in full before the `capture_items` row is physically
// removed, so the contractual baseline remains recoverable from this
// table (it is the only place the deleted count survives). The
// AGENTS.md "Business criticality" amendment points here.
//
// Why a separate table rather than another soft-delete flag:
//   - `capture_items.rejected_at` already exists for the *reversible*
//     admin Reject (the row stays, hidden). AE3 is a DIFFERENT action —
//     a permanent removal the admin is told is irreversible. Keeping
//     the snapshot in a sibling table keeps the live `capture_items`
//     table free of tombstones and keeps every existing consumer query
//     (review queue / CSV export / dashboards / breakdowns) unchanged:
//     a deleted row is simply gone, not filtered.
//   - The archive is an INTERNAL forensic trail, not a user-facing
//     undo. Nothing reads it back into the app; recovery is a manual
//     DB operation.
//
// Split out of `./schema.ts` (like schema-capture-items.ts) to keep
// that file within the 500-line policy cap, and re-exported from
// `@citycare/shared` via index.ts so the import path is unchanged.
//
// Columns mirror EVERY `capture_items` column (see
// ./schema-capture-items.ts — the source of truth for the types) so a
// deleted row can be reconstructed verbatim, plus deletion metadata.
// schema-deleted-capture-items.parity.test.ts pins that mirror: a column
// added to capture_items without its archive twin fails the unit run.
//
// NO foreign keys back to the source tables (capture_sessions, sites,
// buildings, spaces, asset_types): the whole point is that the
// referenced rows may themselves be gone or change, and an archive of a
// deleted count must never be blocked or cascaded by the live graph.
// The single FK is `deleted_by → users.id` (restrict), mirroring
// schema-exclusions.ts's `dismissed_by` — the acting admin is a live
// user we want preserved for the audit trail, and restrict stops a user
// row being removed while it still owns archive entries.
//
// PowerSync: this table is deliberately NOT added to the client mirror
// (apps/frontend/src/hooks/powersync/schema.ts) or sync-config.yaml.
// Syncing it would re-leak deleted contractual snapshots back to field
// devices. The schema-drift gate only inspects tables declared in the
// client mirror, so an unsynced table is correctly ignored by it.

import { sql } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core'

import { users } from './schema'

export const deletedCaptureItems = pgTable(
  'deleted_capture_items',
  {
    // New archive-row identity — independent of the deleted row's id so
    // the same original_item_id could (in principle) be archived twice
    // without a PK clash, and so the archive id is never confused with a
    // live capture_items id.
    id: uuid('id').primaryKey().defaultRandom(),

    // --- mirror of capture_items, carrying the deleted row verbatim ---
    // `original_item_id` is the deleted capture_items.id. Indexed (below)
    // so an operator can find the archive entry for a known item id.
    originalItemId: uuid('original_item_id').notNull(),
    captureSessionId: uuid('capture_session_id').notNull(),
    // The deleted row's denormalised owner (capture_items.user_id, migration
    // 0021 — the per-user sync-bucket key). Nullable ONLY because archive
    // rows written before migration 0022 predate the column; the delete
    // path always copies it. No FK (see the header: the archive never
    // depends on the live graph) — audit-1-#9 on PR #852.
    userId: uuid('user_id'),
    siteId: uuid('site_id').notNull(),
    buildingId: uuid('building_id'),
    spaceId: uuid('space_id'),
    assetTypeId: uuid('asset_type_id').notNull(),
    quantity: numeric('quantity', { precision: 10, scale: 2 }).notNull(),
    isZeroConfirmed: boolean('is_zero_confirmed').notNull(),
    notes: text('notes'),
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull(),
    // The live row's updated_at at delete time, preserved under a
    // distinct name so it is never mistaken for the archive row's own
    // deleted_at write timestamp.
    originalUpdatedAt: timestamp('original_updated_at', {
      withTimezone: true,
    }).notNull(),
    syncVersion: integer('sync_version').notNull(),
    // The live row's rejected_at, if it had been soft-rejected before
    // the hard delete (nullable on the source, nullable here).
    originalRejectedAt: timestamp('original_rejected_at', {
      withTimezone: true,
    }),

    // --- deletion metadata -------------------------------------------
    // The admin who performed the delete. Mirrors schema-exclusions.ts
    // dismissed_by: FK to users.id, restrict so the acting user cannot
    // be removed while they still own archive rows.
    deletedBy: uuid('deleted_by')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    deletedAt: timestamp('deleted_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    // Optional free-text justification supplied by the admin at delete
    // time (e.g. "duplicate count"). Nullable — the reason is optional.
    reason: text('reason'),
  },
  (t) => ({
    originalItemIdx: index('deleted_capture_items_original_item_id_idx').on(
      t.originalItemId,
    ),
    buildingIdx: index('deleted_capture_items_building_id_idx')
      .on(t.buildingId)
      .where(sql`${t.buildingId} IS NOT NULL`),
  }),
)
